/**
 * Pure helpers for the freshness-probe lag-history bar series.
 *
 * Intentionally dependency-free (no React, no DOM). Tested via vitest in
 * `lag-bars.test.ts`. Component layer (`LagHistoryBar.tsx`) consumes these.
 *
 * Threshold values are mirrored from
 * `the autotests suites/tests/health-check/constants.ts` (`FRESHNESS_LAG_THRESHOLDS`).
 * If the canonical thresholds change there, mirror the change here.
 * Long-term: backend should expose thresholds in EndpointSummaryDto so the
 * frontend can stop maintaining a parallel copy.
 */

export type Pipeline =
  | 'masterchain'
  | 'actions'
  | 'traces'
  | 'transactions'
  | 'unknown';

export interface LagThresholds {
  readonly ok: number;
  readonly warn: number;
}

/** Mirror of the e2e-tests `FRESHNESS_LAG_THRESHOLDS` constant. */
export const FRESHNESS_LAG_THRESHOLDS: Record<Pipeline, LagThresholds> = {
  masterchain: { ok: 15, warn: 60 },
  actions: { ok: 30, warn: 120 },
  traces: { ok: 30, warn: 120 },
  transactions: { ok: 30, warn: 120 },
  // Fallback for unrecognized endpoint shapes — uses the masterchain
  // (strictest) thresholds. Better to over-warn than miss a problem.
  unknown: { ok: 15, warn: 60 },
};

/**
 * Minimum value of the per-row dynamic ceiling. Acts as a div-by-zero
 * guard for an all-null/all-zero row — no semantic floor on the height.
 * Set deliberately low (1s) so the dynamic scale truly adapts to the row's
 * own range: a row whose peak is 4s renders 4s near the top, not in the
 * lower quarter. Trade-off: a fully-healthy 1s reading also gets close to
 * full height — that is intentional, the user's mental model is "tallest
 * bar = this row's worst recent moment", not "tall bar = absolute trouble".
 * Severity color (lagToTone) still encodes absolute thresholds.
 */
export const LAG_HEIGHT_MIN_CLAMP_SEC = 1;

/**
 * Minimum bar height (% of row) for a non-null lag value. 0-lag samples
 * still render visibly so operators see "data is here, healthy" rather
 * than mistaking it for no_data.
 */
export const LAG_BAR_FLOOR_PCT = 12.5;

/**
 * Per-row dynamic ceiling padding factor — the bar that hits the row's
 * peak p95 renders at `100% / DYN_CLAMP_PADDING` of full height, leaving
 * headroom so the peak is visually distinguishable from a saturated bar.
 * 1.2 means the peak fills ~83% of the row.
 */
export const LAG_DYN_CLAMP_PADDING = 1.2;

/**
 * Infer the pipeline from a freshness-probe endpoint key. The endpoint key
 * convention is `<METHOD> <path> (freshness)` — we string-match on the path
 * tail. Returns `'unknown'` if the path doesn't match a known pipeline.
 */
export function pipelineFromEndpointKey(endpointKey: string): Pipeline {
  if (endpointKey.includes('/masterchainInfo')) return 'masterchain';
  if (endpointKey.includes('/actions')) return 'actions';
  if (endpointKey.includes('/traces')) return 'traces';
  if (endpointKey.includes('/transactions')) return 'transactions';
  return 'unknown';
}

export type LagTone = 'ok' | 'warn' | 'down' | 'none';

/**
 * Classify a lag value (in seconds) for a given pipeline. `null` → `none`.
 * Negative values (clock skew) clamp to `ok` because the first branch
 * `lagSec <= ok` matches.
 */
export function lagToTone(
  lagSec: number | null,
  pipeline: Pipeline
): LagTone {
  if (lagSec === null) return 'none';
  const t = FRESHNESS_LAG_THRESHOLDS[pipeline];
  if (lagSec <= t.ok) return 'ok';
  if (lagSec <= t.warn) return 'warn';
  return 'down';
}

/**
 * Compute a per-row dynamic ceiling for the lag bars: the row's peak p95
 * times {@link LAG_DYN_CLAMP_PADDING}, floored at {@link LAG_HEIGHT_MIN_CLAMP_SEC}.
 *
 * Why per-row dynamic instead of a fixed 7200s clamp: with a fixed 2h ceiling,
 * mainnet's healthy 1-10s zone compressed into the bottom ~25% of the bar,
 * making 3s and 8s visually indistinguishable. With a row-local ceiling the
 * relative shape of the row's variation occupies the full bar height — the
 * user can see "this row's worst was here" and "this bucket was X% of that
 * worst" at a glance.
 *
 * Trade-off: the absolute meaning of bar height is no longer comparable
 * across rows. The {@link lagToTone} severity color stays threshold-based
 * (per-pipeline absolute thresholds), so red/yellow/green still encode
 * absolute severity. Height encodes relative variation within the row.
 */
export function computeDynamicClampSec(
  buckets: readonly { lagP95Sec: number | null }[],
): number {
  let peak = 0;
  for (const b of buckets) {
    if (b.lagP95Sec !== null && b.lagP95Sec > peak) peak = b.lagP95Sec;
  }
  const padded = peak * LAG_DYN_CLAMP_PADDING;
  return padded > LAG_HEIGHT_MIN_CLAMP_SEC
    ? padded
    : LAG_HEIGHT_MIN_CLAMP_SEC;
}

/**
 * Convert a lag value to bar-fill height percentage against a row-specific
 * `clampSec` (typically from {@link computeDynamicClampSec}).
 *
 * Behavior:
 *   - `null` → 100% (no_data cell — full-row stripe distinguishes from "0 lag").
 *   - 0 → `LAG_BAR_FLOOR_PCT` (visible floor, healthy).
 *   - 0 < lag <= clampSec → linear-scaled height between floor and 100%.
 *   - lag > clampSec → 100% (saturated; color encodes severity).
 *
 * Linear (not log) is intentional: with a row-local ceiling the dynamic
 * range is small (typically < 1 order of magnitude), so log compression
 * actively hurts — it would shrink the difference between 3s and 8s back
 * to the same problem we're solving. Linear gives equal vertical real
 * estate to equal absolute differences.
 */
export function lagToHeightPct(
  lagSec: number | null,
  clampSec: number,
): number {
  if (lagSec === null) return 100;
  if (lagSec <= 0) return LAG_BAR_FLOOR_PCT;
  if (lagSec >= clampSec) return 100;
  const linearPct = (lagSec / clampSec) * 100;
  return linearPct > LAG_BAR_FLOOR_PCT ? linearPct : LAG_BAR_FLOOR_PCT;
}
